/**
 * Request context on the REPORT page (admin-only) — one COMPACT
 * horizontal bar above the viewer:
 *
 *   Alpha Team · EDD Standard · client@x.com · Freshdesk #123 · £2,950 ·
 *   10d · ⚡ │ Backoffice #517 ↗
 *
 * The report is the deliverable surface: context must not cost reading
 * width, so no rail column and no activity log — just the facts and the
 * external Backoffice link. The request's attached documents live in the
 * aside's Documents tab ("Project documents" section).
 * Renders nothing for self-served reports (no parent request).
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Anchor, Badge, Card, Group, Text } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';

import { BackofficeBehindNotice } from '@/components/BackofficeBehindNotice';
import { config } from '@/config/environment';
import { useTeams } from '@/hooks';
import { useReportRequestTriage } from '@/hooks/useReportRequests';
import { formatRequestSource } from '@/types/report-request';
import { formatDueDate, formatMoney } from '@/utils';

interface RequestContextBarProps {
  reportRequestId: string;
  /** From the report meta the page already holds — no extra fetch. */
  backofficeProjectId?: string | null;
  /** The report's effective due date; once set it replaces the raw TAT
   * fact (a started report is on the clock, not on an agreement). */
  dueAt?: string | null;
}

export function RequestContextBar({
  reportRequestId,
  backofficeProjectId,
  dueAt,
}: Readonly<RequestContextBarProps>) {
  const { detail } = useReportRequestTriage(reportRequestId);
  const { teams } = useTeams();
  const teamName = useMemo(() => {
    const team = teams.find((t) => String(t.id) === String(detail?.teamId ?? ''));
    return team?.name ?? null;
  }, [teams, detail?.teamId]);

  if (!detail) {
    return null;
  }

  const dueFact = dueAt
    ? `Due ${formatDueDate(dueAt)}`
    : detail.turnaroundTimeDays != null
      ? `${detail.turnaroundTimeDays}d TAT`
      : null;
  const facts: string[] = [
    teamName ?? detail.teamId ?? 'No team',
    ...(detail.requesterEmail ? [detail.requesterEmail] : []),
    formatRequestSource(detail.source) +
      (detail.freshdeskTicketId ? ` #${detail.freshdeskTicketId}` : ''),
    ...(detail.price ? [formatMoney(detail.price, detail.currency) ?? detail.price] : []),
    ...(dueFact ? [dueFact] : []),
  ];

  return (
    <Card shadow="sm" padding="xs" radius="md" data-testid="request-context-bar">
      <Group gap={6} wrap="wrap">
        {facts.map((fact, i) => (
          <Group key={fact} gap={6} wrap="nowrap">
            {i > 0 && (
              <Text size="xs" c="dimmed">
                ·
              </Text>
            )}
            <Text size="xs">{fact}</Text>
          </Group>
        ))}
        {detail.configName && detail.reportConfigId && (
          <Group gap={6} wrap="nowrap">
            <Text size="xs" c="dimmed">
              ·
            </Text>
            {/* Linked — clients often have similarly-named configs; one
                click confirms which one this actually is. */}
            <Anchor
              component={Link}
              to={`/admin/report-configs/${detail.reportConfigId}`}
              size="xs"
              data-testid="context-config-link"
            >
              {detail.configName}
            </Anchor>
          </Group>
        )}
        {detail.expedited && (
          <Badge size="xs" color="orange" variant="light">
            ⚡ Expedited
          </Badge>
        )}
        <BackofficeBehindNotice marker={detail.backofficeSyncMarker} what="price and due date" />

        {backofficeProjectId && (
          <>
            <Text size="xs" c="dimmed">
              │
            </Text>
            <Anchor
              href={`${config.backofficeAdminUrl}/admin/project/${backofficeProjectId}`}
              target="_blank"
              rel="noreferrer"
              size="xs"
              data-testid="backoffice-project-link"
            >
              <Group gap={4} wrap="nowrap">
                Backoffice #{backofficeProjectId}
                <IconExternalLink size={11} />
              </Group>
            </Anchor>
          </>
        )}
      </Group>
    </Card>
  );
}
